import { Controller } from "@hotwired/stimulus";

export default class MultiselectController extends Controller {
  static targets = ["trigger", "display", "hiddenContainer", "hiddenInput", "option", "popover"];
  static values = {
    selected: Array,
    overflow: String,
    name: String,
  };

  connect() {
    this.updateDisplay();
  }

  toggleDropdown(event) {
    event.preventDefault();
    // The popover is handled by the popover component
  }

  preventSubmit(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  toggleOption(event) {
    event.preventDefault();
    const optionElement = event.currentTarget;
    const value = optionElement.dataset.multiselectValue;

    if (this.selectedValue.includes(value)) {
      this.removeSelection(value);
    } else {
      this.addSelection(value);
    }

    this.updateCheckmarks();
    this.updateDisplay();
    this.updateHiddenInputs();
    this.dispatchChangeEvent();
  }

  addSelection(value) {
    if (!this.selectedValue.includes(value)) {
      this.selectedValue = [...this.selectedValue, value];
    }
  }

  removeSelection(value) {
    this.selectedValue = this.selectedValue.filter((v) => v !== value);
  }

  updateCheckmarks() {
    this.optionTargets.forEach((option) => {
      const value = option.dataset.multiselectValue;
      const checkmark = option.querySelector(".w-4.h-4");
      const isSelected = this.selectedValue.includes(value);

      if (isSelected) {
        if (!checkmark.querySelector("svg")) {
          checkmark.innerHTML =
            '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>';
        }
      } else {
        checkmark.innerHTML = "";
      }
    });
  }

  updateDisplay() {
    const displayElement = this.displayTarget;

    if (this.selectedValue.length === 0) {
      displayElement.innerHTML = '<span class="text-muted-foreground">Select items...</span>';
      this.updatePopoverPosition();
      return;
    }

    const selectedLabels = this.selectedValue.map((value) => {
      const option = this.optionTargets.find((opt) => opt.dataset.multiselectValue === value);
      return option ? option.querySelector("span").textContent : value;
    });

    if (this.overflowValue === "truncate" && this.selectedValue.length > 3) {
      const totalItems = this.optionTargets.length;
      displayElement.innerHTML = `${this.selectedValue.length}/${totalItems} selected`;
    } else {
      const badgeClass =
        this.overflowValue === "wrap" ? "flex flex-wrap gap-1" : "flex items-center gap-1";
      const badges = selectedLabels
        .map(
          (label) =>
            `<span class="inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold bg-secondary text-secondary-foreground">${label}</span>`
        )
        .join("");

      displayElement.innerHTML = `<div class="${badgeClass}">${badges}</div>`;
    }
    this.updatePopoverPosition();
  }

  updatePopoverPosition() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event("resize"));
      });
    });
  }

  updateHiddenInputs() {
    this.hiddenContainerTarget.innerHTML = "";

    this.selectedValue.forEach((value) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = this.getInputName();
      input.value = value;
      input.dataset.multiselectTarget = "hiddenInput";
      this.hiddenContainerTarget.appendChild(input);
    });
  }

  getInputName() {
    const namePattern = this.nameValue || "multiselect[]";
    return namePattern.endsWith("[]") ? namePattern : `${namePattern}[]`;
  }

  // Methods that other controllers can use by connecting via outlets
  getSelectedValues() {
    return [...this.selectedValue];
  }

  setSelectedValues(values) {
    this.selectedValue = Array.isArray(values) ? values : [values];
    this.updateCheckmarks();
    this.updateDisplay();
    this.updateHiddenInputs();
  }

  clearSelections() {
    this.selectedValue = [];
    this.updateCheckmarks();
    this.updateDisplay();
    this.updateHiddenInputs();
  }

  setOptions(newOptions, preserveSelections = false) {
    const optionsContainer = this.element.querySelector('[role="group"]');
    if (!optionsContainer) return;

    const currentSelections = this.getSelectedValues();

    optionsContainer.innerHTML = "";

    newOptions.forEach((option) => {
      const optionElement = this.createOptionElement(option.value, option.label, false);
      const wrapper = document.createElement("div");
      wrapper.setAttribute("data-ui--filter-target", "item");
      wrapper.appendChild(optionElement);
      optionsContainer.appendChild(wrapper);
    });

    if (preserveSelections) {
      const availableValues = newOptions.map((opt) => opt.value);
      const validSelections = currentSelections.filter((value) => availableValues.includes(value));
      this.setSelectedValues(validSelections);
    } else {
      this.clearSelections();
    }
  }

  createOptionElement(value, label, selected) {
    const optionElement = document.createElement("div");
    optionElement.className =
      "flex items-center space-x-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground";
    optionElement.setAttribute("data-multiselect-target", "option");
    optionElement.setAttribute("data-multiselect-value", value);
    optionElement.setAttribute("data-action", "click->multiselect#toggleOption");

    const checkmarkContainer = document.createElement("div");
    checkmarkContainer.className = "w-4 h-4 text-brand-500";
    if (selected) {
      checkmarkContainer.innerHTML =
        '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>';
    }

    const labelSpan = document.createElement("span");
    labelSpan.className = "flex-1";
    labelSpan.textContent = label;

    optionElement.appendChild(checkmarkContainer);
    optionElement.appendChild(labelSpan);

    return optionElement;
  }

  dispatchChangeEvent() {
    // Dispatch change event so it behaves like a standard form element
    const changeEvent = new Event("change", { bubbles: true });
    this.hiddenContainerTarget.dispatchEvent(changeEvent);
  }
}
